import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, MessageCircle, Truck, Sparkles, ShieldCheck, Search, ShoppingBag, ArrowRight } from "lucide-react";
import { api, fileUrl, waLink, generalMessage } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductModal } from "@/components/ProductModal";
import { ReviewsSection } from "@/components/ReviewsSection";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_number: "", instagram_url: "https://www.instagram.com/iralakshmi.fashion/", offer_text: "" });
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const filtered = useMemo(() => {
    let list = products.filter(
      (p) =>
        (category === "All" || p.category === category) &&
        (!search.trim() || (p.name + " " + p.description).toLowerCase().includes(search.trim().toLowerCase()))
    );
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, category, search, sort]);

  const generalWa = waLink(settings.whatsapp_number, generalMessage);
  const heroImage = products.find((p) => p.category === "Sarees")?.image_url || "";

  return (
    <div className="min-h-screen bg-ivory font-body">
      {/* Header */}
      <header data-testid="shop-header" className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-md border-b border-gold-line">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 sm:px-8 lg:px-12 h-16 sm:h-[72px]">
          <a href="/" data-testid="header-brand-link" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Ira Lakshmi logo" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-1 ring-gold/60 shadow-sm" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-2xl sm:text-3xl font-bold text-maroon-deep tracking-tight">Ira Lakshmi</span>
              <span className="text-[10px] sm:text-[11px] text-gold-text font-semibold tracking-wide mt-1">Designer Collection by Neetu</span>
            </span>
          </a>
          <div className="flex items-center gap-2 sm:gap-4">
            <a
              data-testid="header-instagram-link"
              href={settings.instagram_url || "https://www.instagram.com/iralakshmi.fashion/"}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 text-ink-muted hover:text-maroon transition-colors duration-200"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm font-medium">@iralakshmi.fashion</span>
            </a>
            <a
              data-testid="header-whatsapp-btn"
              href={generalWa}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-whats hover:bg-whats-dark text-white px-4 sm:px-5 py-2.5 text-sm font-semibold shadow-md transition-colors duration-200"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp Order</span>
              <span className="sm:hidden">Order</span>
            </a>
            <Link data-testid="admin-header-link" to="/admin" title="Admin Panel" className="p-2 rounded-full text-ink-muted hover:text-maroon hover:bg-gold-soft/60 transition-colors duration-200">
              <ShieldCheck className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pt-6 sm:pt-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="gold-frame relative overflow-hidden rounded-3xl bg-gradient-to-br from-maroon-deep via-maroon to-maroon-dark text-white shadow-[0_24px_60px_-24px_rgba(74,17,29,0.5)]"
        >
          <div className="hero-glow absolute inset-0 pointer-events-none" aria-hidden="true" />
          <span className="corner-ornament left-4 top-4" aria-hidden="true" />
          <span className="corner-ornament right-4 top-4" aria-hidden="true" />
          <span className="corner-ornament left-4 bottom-4" aria-hidden="true" />
          <span className="corner-ornament right-4 bottom-4" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center p-6 sm:p-10 lg:p-14">
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <span className="hairline-gold w-10" aria-hidden="true" />
                <p data-testid="hero-eyebrow" className="font-accent text-[11px] sm:text-sm tracking-[0.3em] uppercase text-gold">
                  Ira Lakshmi • by Neetu
                </p>
              </div>
              <h1 data-testid="hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.08] tracking-tight">
                The Perfect Look for <span className="italic text-gold">Every Occasion</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed max-w-lg">
                Handpicked sarees and kurtis in fabrics that feel as good as they look. See something you love? Order in one tap — straight to WhatsApp.
              </p>
              {settings.offer_text ? (
                <div data-testid="hero-offer-chip" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/50 px-4 py-2 text-sm font-medium text-gold-soft">
                  <Sparkles className="w-4 h-4" />
                  {settings.offer_text}
                </div>
              ) : null}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  data-testid="hero-cta-collection"
                  href="#collection"
                  className="group inline-flex items-center gap-2 rounded-full bg-gold text-maroon-deep px-7 py-3 font-semibold shadow-[0_10px_24px_-8px_rgba(212,175,55,0.6)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Browse Collection
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
                <a
                  data-testid="hero-cta-whatsapp"
                  href={generalWa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-whats/80 bg-whats/15 px-7 py-3 font-semibold text-white transition-colors duration-200 hover:bg-whats hover:border-whats"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
            <div className="relative hidden sm:block">
              {heroImage ? (
                <div className="relative rounded-2xl p-2 border border-gold/40 bg-white/5 backdrop-blur-sm">
                  <img
                    data-testid="hero-image"
                    src={fileUrl(heroImage)}
                    alt="Festive collection"
                    className="w-full h-[340px] lg:h-[430px] object-cover rounded-xl"
                  />
                  <div className="absolute -bottom-5 -left-6 flex items-center gap-3 rounded-xl bg-white/95 text-ink shadow-xl border border-gold/40 px-4 py-3 backdrop-blur-sm">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-whats/15 text-whats-dark">
                      <MessageCircle className="w-5 h-5" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">One tap = direct order</p>
                      <p className="text-xs text-ink-muted">Instant replies on WhatsApp</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Trust strip */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 mt-8 sm:mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
          {[
            { icon: MessageCircle, title: "Direct WhatsApp Order", sub: "Talk directly to the shop owner" },
            { icon: Truck, title: "Pan-India Delivery", sub: "Safe delivery to your doorstep" },
            { icon: Sparkles, title: "Handpicked Quality", sub: "Every piece quality-checked" },
          ].map((t) => (
            <div key={t.title} className="flex items-center gap-3 rounded-2xl bg-white border border-gold-line px-5 py-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-soft text-maroon">
                <t.icon className="w-5 h-5" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-ink">{t.title}</p>
                <p className="text-xs text-ink-muted">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Collection */}
      <section id="collection" className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-10 sm:py-16 scroll-mt-20">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-accent text-xs tracking-[0.3em] uppercase text-gold-text mb-2">Boutique Collection</p>
            <h2 data-testid="collection-heading" className="font-display text-3xl sm:text-4xl font-semibold text-maroon-dark tracking-tight">
              New Collection
            </h2>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <input
                data-testid="product-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sarees, kurtis..."
                className="w-full rounded-full border border-gold-line bg-white pl-10 pr-4 py-2.5 text-sm outline-none placeholder:text-ink-muted/60 focus:border-gold"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="rounded-full border-gold-line bg-white text-sm w-[150px]">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="low">Price: Low to High</SelectItem>
                <SelectItem value="high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div data-testid="category-pill-bar" className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`category-pill-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full border px-5 py-2 text-sm font-medium transition-all duration-200 ${
                category === c
                  ? "bg-maroon border-maroon text-white shadow-md"
                  : "bg-white border-gold-line text-ink hover:border-gold hover:text-maroon"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="motif-line mt-8 mb-8" />

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[3/4] w-full rounded-2xl" />
                <Skeleton className="h-4 w-3/4 rounded-full" />
                <Skeleton className="h-4 w-1/3 rounded-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="flex flex-col items-center gap-3 py-16 text-center border border-dashed border-gold-line rounded-3xl bg-white/60">
            <ShoppingBag className="w-10 h-10 text-gold" />
            <p className="font-display text-2xl text-maroon-dark">No products found</p>
            <p className="text-sm text-ink-muted max-w-sm">Try a different search or category — or message us on WhatsApp, new stock arrives often!</p>
          </div>
        ) : (
          <div data-testid="product-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} whatsappNumber={settings.whatsapp_number} onOpen={() => setActive(p)} />
            ))}
          </div>
        )}
      </section>

      <ReviewsSection />

      {/* Footer */}
      <footer data-testid="shop-footer" className="relative overflow-hidden bg-maroon-deep text-white">
        <div className="hero-glow absolute inset-0 opacity-50 pointer-events-none" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
          <div className="grid sm:grid-cols-3 gap-10 items-start">
            <div className="text-center sm:text-left">
              <img src="/logo-full.jpg" alt="Ira Lakshmi — Designer Collection by Neetu" className="h-28 rounded-xl ring-1 ring-gold/50 shadow-lg mx-auto sm:mx-0" />
              <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-gold mt-4">Designer Collection by Neetu</p>
            </div>
            <div className="text-center sm:text-left">
              <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-gold mb-4">Explore</p>
              <div className="flex flex-col items-center sm:items-start gap-2.5">
                <a href="#collection" className="text-sm text-white/80 hover:text-gold transition-colors duration-200">New Collection</a>
                <a data-testid="instagram-footer-link" href={settings.instagram_url || "https://www.instagram.com/iralakshmi.fashion/"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold transition-colors duration-200">
                  <Instagram className="w-4 h-4" />
                  Follow on Instagram
                </a>
                <a href="#reviews" className="text-sm text-white/80 hover:text-gold transition-colors duration-200">Ratings &amp; Reviews</a>
              </div>
            </div>
            <div className="text-center sm:text-left">
              <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-gold mb-4">Order &amp; Contact</p>
              <p className="text-sm text-white/75 leading-relaxed mb-4">Every order goes straight to our WhatsApp — we reply personally and ship pan-India.</p>
              <a
                data-testid="footer-whatsapp-btn"
                href={generalWa}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-whats px-6 py-2.5 text-sm font-semibold text-white hover:bg-whats-dark transition-colors duration-200"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Order
              </a>
            </div>
          </div>
          <div className="motif-line my-10 opacity-70" />
          <p className="text-center text-xs text-white/60">© {new Date().getFullYear()} Ira Lakshmi — Designer Collection by Neetu • All rights reserved</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        data-testid="floating-whatsapp-btn"
        href={generalWa}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-whats text-white shadow-2xl hover:bg-whats-dark hover:scale-105 transition-all duration-200"
        title="WhatsApp par order karein"
      >
        <MessageCircle className="w-7 h-7" />
      </a>

      <ProductModal product={active} onClose={() => setActive(null)} whatsappNumber={settings.whatsapp_number} />
    </div>
  );
}
